import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Loader from './Loader';
import { FlyerType, useFlyerGenerationGate } from '../../hooks/useFlyerGenerationGate';
import { RootState } from '../../store/store';

type RowState = 'running' | 'ready' | 'failed';

interface RowView {
  type: FlyerType;
  state: RowState;
  progress: number;
  url: string | null;
  title: string;
}

const ACK_STORAGE_KEY = 'flyerGenerationAcknowledgedUrls';

const TITLES: Record<FlyerType, { running: string; ready: string; failed: string }> = {
  pre: {
    running: 'Generating pre-session flyers',
    ready: 'Pre-session flyers ready',
    failed: 'Pre-session flyers failed',
  },
  post: {
    running: 'Generating post-session flyers',
    ready: 'Post-session flyers ready',
    failed: 'Post-session flyers failed',
  },
};

const readAcknowledgedUrls = (): string[] => {
  try {
    const raw = localStorage.getItem(ACK_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const persistAcknowledgedUrls = (urls: string[]) => {
  try {
    localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(urls.slice(-20)));
  } catch {
    // ignore quota errors
  }
};

const useRowView = (type: FlyerType, acknowledgedUrls: string[], dismissed: boolean): RowView | null => {
  const { isRunning, progress, url, isStale } = useFlyerGenerationGate(type);
  const titles = TITLES[type];

  if (isStale) {
    return { type, state: 'failed', progress: progress || 0, url: null, title: titles.failed };
  }
  if (isRunning) {
    return { type, state: 'running', progress: progress || 0, url: null, title: titles.running };
  }
  if (url && !acknowledgedUrls.includes(url) && !dismissed) {
    return { type, state: 'ready', progress: 100, url, title: titles.ready };
  }
  return null;
};

const FlyerGenerationSnackbar: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);

  const [acknowledgedUrls, setAcknowledgedUrls] = useState<string[]>(() => readAcknowledgedUrls());
  const [dismissedByType, setDismissedByType] = useState<Record<FlyerType, boolean>>({ pre: false, post: false });
  const [minimized, setMinimized] = useState(false);

  const preRow = useRowView('pre', acknowledgedUrls, dismissedByType.pre);
  const postRow = useRowView('post', acknowledgedUrls, dismissedByType.post);

  React.useEffect(() => {
    if (preRow?.state === 'running' && dismissedByType.pre) {
      setDismissedByType((prev) => ({ ...prev, pre: false }));
    }
  }, [preRow?.state, dismissedByType.pre]);
  React.useEffect(() => {
    if (postRow?.state === 'running' && dismissedByType.post) {
      setDismissedByType((prev) => ({ ...prev, post: false }));
    }
  }, [postRow?.state, dismissedByType.post]);

  if (!user?.isTeacherMode) return null;

  const rows: RowView[] = [preRow, postRow].filter((r): r is RowView => r !== null);
  if (rows.length === 0) return null;

  const acknowledgeUrl = (url: string | null) => {
    if (!url) return;
    const next = [...acknowledgedUrls.filter((u) => u !== url), url];
    setAcknowledgedUrls(next);
    persistAcknowledgedUrls(next);
  };

  const dismissRow = (type: FlyerType, url: string | null) => {
    acknowledgeUrl(url);
    setDismissedByType((prev) => ({ ...prev, [type]: true }));
  };

  const dismissAll = () => {
    rows.forEach((r) => acknowledgeUrl(r.url));
    setDismissedByType({ pre: true, post: true });
  };

  const runningCount = rows.filter((r) => r.state === 'running').length;
  const aggregateProgress = runningCount > 0
    ? Math.round(rows.filter((r) => r.state === 'running').reduce((sum, r) => sum + r.progress, 0) / runningCount)
    : 100;

  // Positioned above the Crisp chat widget (bottom-right) on desktop,
  // full-width at the top of the safe area on mobile.
  const containerBase =
    'fixed z-30 bg-white shadow-md rounded-3xl ' +
    'left-4 right-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-24 sm:w-[420px]';

  if (minimized) {
    return (
      <div className={containerBase} role="status" aria-live="polite">
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-gray-50 rounded-3xl"
          aria-label="Expand flyer generation status"
        >
          <div className="flex items-center gap-3">
            {runningCount > 0 && <Loader loader={true} dimentions="w-5 h-5" />}
            <span className="text-sm font-bold text-storyblue">
              {runningCount > 0
                ? `Generating files · ${aggregateProgress}%`
                : 'Files ready to download'}
            </span>
          </div>
          <FontAwesomeIcon icon={faChevronUp} className="text-storyblue" />
        </button>
      </div>
    );
  }

  return (
    <div className={containerBase} role="status" aria-live="polite">
      <div className="flex items-center justify-between px-6 pt-4 pb-2">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
          Generating files
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="p-1.5 rounded-full hover:bg-gray-100 text-storyblue"
            aria-label="Minimize"
            title="Minimize"
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
          <button
            type="button"
            onClick={dismissAll}
            className="p-1.5 rounded-full hover:bg-gray-100 text-storyblue"
            aria-label="Dismiss all"
            title="Dismiss"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
      </div>
      <ul className="divide-y">
        {rows.map((row) => (
          <li key={row.type} className="px-6 py-4">
            {row.state === 'running' && (
              <div className="flex items-center gap-3">
                <Loader loader={true} dimentions="w-5 h-5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-storyblue truncate">{row.title}</div>
                  <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondaryStoryAdditionalLightBlueText transition-all"
                      style={{ width: `${Math.round(row.progress)}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs font-bold text-storyblue tabular-nums">
                  {Math.round(row.progress)}%
                </span>
              </div>
            )}
            {row.state === 'ready' && row.url && (
              <div className="flex items-center gap-3" aria-live="assertive">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-storyblue truncate">{row.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Your flyers are ready to download</div>
                </div>
                <a
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download flyers"
                  className="text-secondaryStoryAdditionalLightBlueText bg-transparent border border-secondaryStoryAdditionalLightBlueText rounded-2xl hover:shadow-md pl-2 pr-4 py-1 text-sm font-bold flex items-center h-9 whitespace-nowrap"
                >
                  <img
                    src="/download_icon_transparent.png"
                    alt=""
                    className="w-8 h-8 p-1.5"
                  />
                  Download
                </a>
                <button
                  type="button"
                  onClick={() => dismissRow(row.type, row.url)}
                  className="p-1.5 rounded-full hover:bg-gray-100 text-storyblue"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
            )}
            {row.state === 'failed' && (
              <div className="flex items-center gap-3" aria-live="assertive">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-red-600 truncate">{row.title}</div>
                  <div className="text-xs text-red-500 mt-0.5">Please try again from the original page</div>
                </div>
                <button
                  type="button"
                  onClick={() => dismissRow(row.type, null)}
                  className="p-1.5 rounded-full hover:bg-red-50 text-red-500"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FlyerGenerationSnackbar;
